import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Button from 'srcReact/components/common2/Button'
import Calendar from 'srcReact/components/common2/Calendar'
import RadioButtonGroup from 'srcReact/components/common2/RadioButtonGroup'
import { convertUTCToTimezone, formatLastUpdateDate, roundTimeQuarterHour } from 'srcReact/helpers/datetimeHelper'
import { mixrActions } from 'srcReact/Mixr/actions'
import PureTitle from '../PureTitle'
import styles from './styles.module.scss'
import parentStyles from '../../styles.module.scss'
import TimePicker from 'srcReact/components/common2/TimePicker'
import dayjs from 'dayjs'
import Spinner from 'srcReact/components/common2/Spinner'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'

function InviteMembers({ editMode, handleShowPreview, setHasFormErrors, showInviteSettings }) {
  //inviteDateToUpdate
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [selectedOption, setSelectedOption] = useState(0)
  const [inviteDate, setInviteDate] = useState('')
  const [inviteTime, setInviteTime] = useState('')
  const [formattedInviteDate, setFormattedInviteDate] = useState('')
  const [valueChanged, setValueChanged] = useState(false)
  const [error, setError] = useState('')
  const options = [
    { value: 0, label: t('event.create.inviteMembers.sendNow') },
    { value: 1, label: t('event.create.inviteMembers.sendLater') }
  ]
  const {
    common: { isMobile, isNative },
    mixr: { mixr },
    mixr: {
      mixr: {
        id: mixrId,
        is_invite_scheduled,
        local_invite_send_date,
        local_start_at: eventStartDate,
        location: mixrLocation
      },
      editLoading,
      editSuccess
    }
  } = useSelector((state) => state)

  useEffect(() => {
    if (!editMode) {
      getNextHour()
    }
  }, [])
  useEffect(() => {
    if (mixrLocation?.timezone) {
      getNextHour()
    }
  }, [mixrLocation?.timezone])

  useEffect(() => {
    if (editMode && editSuccess) {
      setValueChanged(false)
    }
  }, [editSuccess])

  const getNextHour = () => {
    const currentDatetimeTZ = getCurrentDateAsTimezone()
    const startValue = roundTimeQuarterHour(currentDatetimeTZ)
    setInviteTime(startValue)
  }

  useEffect(() => {
    setTimeout(populateData, 500) //this timeout is applied so that it is executed, once timezone is applied
  }, [local_invite_send_date])

  const populateData = () => {
    if (local_invite_send_date && local_invite_send_date.length > 0) {
      setSelectedOption(1)
      const dateToUpdate = new Date(local_invite_send_date)
      let hr = dateToUpdate.getHours()
      let min = dateToUpdate.getMinutes()
      hr = hr < 10 ? `0${hr}` : hr
      min = min < 10 ? `0${min}` : min
      const time = `${hr}:${min}`
      setInviteDate(new Date(local_invite_send_date))
      setInviteTime(time)
      setFormattedInviteDate(local_invite_send_date)
    }
  }

  const handleOptionChange = (e) => {
    setError('')
    setFormattedInviteDate('')
    setInviteDate('')
    setValueChanged(true)
    if (e) {
      const value = e.target.value
      setSelectedOption(value)
      dispatch(
        mixrActions.setMixrData([
          { key: 'is_invite_scheduled', value: parseInt(value) },
          { key: 'local_invite_send_date', value: value === '0' ? '' : formattedInviteDate }
        ])
      )
    }
  }

  const getCurrentDateAsTimezone = () => {
    let currentDateTime = dayjs.utc(new Date().getTime())

    if (mixrLocation?.timezone) {
      currentDateTime = convertUTCToTimezone(
        currentDateTime,
        mixrLocation.timezone.utc_offset || mixrLocation.timezone.UTCOffset
      )
    }
    const currentTZDateTime = new Date(
      `${currentDateTime.year()}-${
        currentDateTime.month() + 1
      }-${currentDateTime.date()} ${currentDateTime.hour()}:${currentDateTime.minute()}`
    )
    return currentTZDateTime
  }

  const isPastDate = (date, time) => {
    const currentDate = getCurrentDateAsTimezone()
    const inputDate = joinDateTime(date, time)
    if (inputDate && inputDate.getTime() < currentDate.getTime()) {
      if (mixrLocation?.timezone) {
        setError(
          t('event.create.inviteMembers.pastDateTZ', {
            selectedTimezone: mixrLocation.timezone.name || mixrLocation.timezone.timeZoneName,
            currentTimeInTimezone: `${currentDate.toLocaleString('en-US', {
              day: 'numeric',
              month: 'long',
              hour: 'numeric',
              minute: 'numeric',
              hour12: true
            })}`
          })
        )
        return true
      } else {
        setError(t('event.create.inviteMembers.pastDate'))
        return true
      }
    }
    return false
  }

  const isAfterMixrDate = (date, time) => {
    const inputDate = joinDateTime(date, time)
    const startDate = new Date(eventStartDate)
    if (inputDate && startDate < inputDate.getTime()) {
      setError(t('event.create.inviteMembers.invalidDate'))
      return true
    }
    return false
  }

  const isDateEmpty = () => {
    if (selectedOption.toString() == '1' && formattedInviteDate.length === 0) {
      setError(t('event.create.inviteMembers.emptyDate'))
      return true
    }
    return false
  }

  const joinDateTime = (date, time) => {
    if (date && time) {
      return new Date(`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${time}`)
    }
    return null
  }

  const getFormattedInviteDate = (date, time) => {
    if (date && time) {
      const fullDate = joinDateTime(date, time)
      const formattedDate = formatLastUpdateDate(fullDate, appConstantSettings.DATE_FORMAT7, t)
      return formattedDate
    }
    return null
  }

  const handleDateChange = (date) => {
    setError('')
    setInviteDate(date)
    const formattedDate = getFormattedInviteDate(date, inviteTime)
    setFormattedInviteDate(formattedDate)
    if (isAfterMixrDate(date, inviteTime)) {
      return
    }

    if (isPastDate(date, inviteTime)) {
      return
    }
    setValueChanged(true)

    if (!editMode) {
      dispatch(
        mixrActions.setMixrData([
          { key: 'is_invite_scheduled', value: parseInt(selectedOption) },
          { key: 'local_invite_send_date', value: formattedDate }
        ])
      )
    }
  }

  const handleTimeChange = (time) => {
    setError('')
    if (inviteDate) {
      const formattedDate = getFormattedInviteDate(inviteDate, time.value)
      setFormattedInviteDate(formattedDate)
      if (isPastDate(inviteDate, time.value)) {
        return
      }
      if (!editMode) {
        dispatch(
          mixrActions.setMixrData([
            { key: 'is_invite_scheduled', value: parseInt(selectedOption) },
            { key: 'local_invite_send_date', value: formattedDate }
          ])
        )
      }
    }
    setValueChanged(true)
    setInviteTime(time.value)
  }

  const validate = () => {
    if (isDateEmpty()) {
      return false
    }

    if (isPastDate(inviteDate, inviteTime)) {
      return false
    }
    if (isAfterMixrDate(inviteDate, inviteTime)) {
      return false
    }
    return true
  }

  const updateInviteDate = () => {
    if (mixrId && validate()) {
      const option = parseInt(selectedOption)
      dispatch(
        mixrActions.editMixr(
          mixrId,
          {
            ...mixr,
            is_invite_scheduled: option,
            ...(formattedInviteDate.length > 0 && option === 1 && { invite_send_date: formattedInviteDate })
          },
          'unique',
          true
        )
      )
    }
  }

  const publishMixr = () => {
    if (validate()) {
      handleShowPreview()
    } else {
      setHasFormErrors(true)
    }
  }

  const handleCancel = () => {
    setValueChanged(false)
    populateData()
  }

  return (
    <React.Fragment>
      <PureTitle
        title={t('event.create.inviteMembers.title')}
        subTitle={t('event.create.inviteMembers.subTitle')}
        titleCss={parentStyles.titleCss}
        subTitleCSS={styles.subTitle}
        isRequired={false}
      />
      <RadioButtonGroup
        items={options}
        selectedItemValue={selectedOption.toString()}
        onChange={handleOptionChange}
        radioContainerCSS={styles.options}
      />
      <div className={styles.calendarWrapper}>
        <div className={styles.dateTimeWrapper}>
          <Calendar
            defaultDate={new Date()}
            date={inviteDate}
            minDate={new Date()}
            showIcon={true}
            handleDateChange={handleDateChange}
            disabled={selectedOption.toString() === '0'}
            containerCSS={styles.calendarContainer}
            calendarCSS={(isMobile || isNative || editMode) && styles.positionTop}
          />
          <div className={styles.timeWrapper}>
            <TimePicker
              menuIsOpen={true}
              time={inviteTime}
              handleTimeChange={handleTimeChange}
              className={`${styles.timePicker} ${editMode && styles.timePickerPositionTop}`}
              isMobile={isMobile}
              disabled={selectedOption.toString() === '0'}
            />
            <div className={styles.timezone}>
              {mixrLocation?.timezone?.abbreviation || mixrLocation?.timezone?.timeZoneAbbreviation}
            </div>
          </div>
        </div>
        {error && error.length > 0 && <ErrorMessage html={error} className={styles.error} />}
        <p className={styles.info}>{t('event.create.inviteMembers.info')}</p>
      </div>

      {/* {!editMode && (
        <div className={parentStyles.summaryContainer}>
          <p className={`${parentStyles.para}`}>{t('event.create.summary1')}</p>
          <p className={`${parentStyles.para}`}>{t('event.create.summary2')}</p>
        </div>
      )} */}

      {editMode && valueChanged && (
        <div className={`${parentStyles.buttons}`}>
          <Button style='secondary' text={t('global.cancel')} handleClick={handleCancel} />
          <Button text={editLoading ? <Spinner /> : t('global.save')} handleClick={updateInviteDate} />
        </div>
      )}

      {/* <div className={styles.buttonWrapper}>
        {!editMode && <Button size='large' text={t('event.create.btnPublishMixR')} handleClick={publishMixr} />}
        {!editMode && <Button size='large' text={t('global.btnNext')} handleClick={publishMixr} />}
      </div> */}
    </React.Fragment>
  )
}

export default InviteMembers
